import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ReportDialog({ open, onOpenChange, targetType, targetId, targetLabel }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/reports/reasons").then(r => setReasons(r.data)).catch(() => {});
    setReason("");
    setDescription("");
  }, [open]);

  const submit = async () => {
    if (!reason) {
      toast.error("Sélectionnez un motif");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        description: description.trim(),
      });
      toast.success("Signalement envoyé. Notre équipe va l'examiner.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Flag className="h-5 w-5" /> Signaler
          </DialogTitle>
          <DialogDescription>
            {targetLabel ? <>Signaler : <strong className="text-zinc-900">{targetLabel}</strong></> : "Indiquez ce qui ne va pas. Notre équipe sera notifiée immédiatement."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>En cas d'urgence (danger physique immédiat), contactez le 911. Le signalement est destiné aux problèmes survenus sur la plateforme.</span>
          </div>

          <div>
            <Label className="text-sm">Motif *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="report-reason-select">
                <SelectValue placeholder="Choisir un motif..." />
              </SelectTrigger>
              <SelectContent>
                {reasons.map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm">Description (optionnelle, max 2000 caractères)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Donnez tout détail utile : dates, comportement constaté, photos déjà partagées, etc."
              className="mt-1 rounded-xl min-h-[100px]"
              maxLength={2000}
              data-testid="report-description-input"
            />
            <p className="text-xs text-zinc-400 mt-1">{description.length}/2000</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-xl" onClick={submit} disabled={submitting || !reason} data-testid="report-submit-btn">
            {submitting ? "Envoi..." : "Envoyer le signalement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
